/**
 * localStorage persistence for grid column filters per service.
 * Key: openmon:grid-filters:{serviceSlug}:{gridType}
 */

import type {
  GridTraceFilters,
  GridSpanFilters,
  GridLogFilters,
  GridDurationFilter,
  GridTextFilter,
} from "../types/filters";

export type GridType = "traces" | "spans" | "logs";

export type GridFilters = GridTraceFilters | GridSpanFilters | GridLogFilters;

const STORAGE_PREFIX = "openmon:grid-filters";

function getStorageKey(serviceSlug: string, gridType: GridType): string {
  return `${STORAGE_PREFIX}:${serviceSlug}:${gridType}`;
}

function safeLocalStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Reads grid filters from localStorage.
 * Returns null if not found, invalid, or storage unavailable.
 */
export function getGridFilters<T extends GridFilters>(
  serviceSlug: string,
  gridType: GridType,
): T | null {
  const storage = safeLocalStorage();
  if (!storage || !serviceSlug) return null;

  try {
    const key = getStorageKey(serviceSlug, gridType);
    const raw = storage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;

    return parsed as T;
  } catch {
    return null;
  }
}

/**
 * Writes grid filters to localStorage.
 * No-op if storage unavailable.
 */
export function setGridFilters(
  serviceSlug: string,
  gridType: GridType,
  filters: GridFilters,
): void {
  const storage = safeLocalStorage();
  if (!storage || !serviceSlug) return;

  try {
    const key = getStorageKey(serviceSlug, gridType);
    storage.setItem(key, JSON.stringify(filters));
  } catch {
    // Ignore quota exceeded or other storage errors
  }
}
